import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {showModal} from '@edna/components';
import {Icon, Row} from '@edna/components/primitives';
import EditIcon from '@edna/icons/edit.svg';

import {BigText} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {useGetBalanceQuery} from 'src/containers/Billing';
import {useGetTenantLeadDetailsQuery} from 'src/containers/LeadCompanyClients/api';
import {EPermission, usePermission} from 'src/containers/User';

import Content from './Content';
import {showCompanyDetailsModal} from './Modals/CompanyDetailsModal';
import ContactDetailsModal from './Modals/ContactDetailsModal';
import {useGetCompanyDetailsQuery} from './api';
import {DEFAULT_COMPANY_DETAILS, DEFAULT_TENANT_LEAD_DETAILS, EDetailsAction} from './definitions';
import * as S from './style';
import {getActionName} from './utils';

const SupportCompanyDetails = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: tenantBalance} = useGetBalanceQuery(supportTenantId ?? skipToken);
  const {data: tenantLeadDetails = DEFAULT_TENANT_LEAD_DETAILS} =
    useGetTenantLeadDetailsQuery(supportTenantId);

  const hasTenantDetailsWritePermission = usePermission(
    EPermission.PERMISSION_TENANT_DETAILS_WRITE,
  );
  const canChangeDetails = !supportTenantId || hasTenantDetailsWritePermission;

  const {
    companyName,
    currencyCode,
    countryCode,
    ogrn,
    documentType,
    taxNumber,
    contactName,
    email,
    phone,
    paymentType,
    maxOverdraft,
    securityDeposit,
    managerResponsibleForCheckingId,
    activateLicenseDebt,
    licensePaymentTerm,
    termBeforeTerminationLicense,
  } = tenantLeadDetails;

  const showContactDetailsModal = React.useCallback(() => {
    showModal((closeModal) => <ContactDetailsModal closeModal={closeModal} />);
  }, []);

  return (
    <>
      <BigText>{t('CompanyDetails:contractInfo')}</BigText>
      <Row>
        <S.SmallStartColumn>
          <S.BigText>{t('CompanyDetails:companyDetails')}</S.BigText>
          <S.DetailsContainer>
            <Content
              countryCode={countryCode}
              details={{
                companyName,
                currencyCode,
                countryCode,
                ogrn,
                documentType,
                taxNumber,
              }}
            />
          </S.DetailsContainer>
          <S.BigText>{t('CompanyDetails:settlementRules')}</S.BigText>
          <S.DetailsContainer>
            <Content
              details={{
                paymentType,
                maxOverdraft: tenantBalance?.maxOverdraft ?? maxOverdraft,
                securityDeposit,
                managerResponsibleForCheckingId,
              }}
            />
          </S.DetailsContainer>
          <S.BigText>{t('CompanyDetails:licenseConditions')}</S.BigText>
          <S.DetailsContainer>
            <Content
              details={{
                activateLicenseDebt,
                licensePaymentTerm,
                termBeforeTerminationLicense,
              }}
            />
          </S.DetailsContainer>
        </S.SmallStartColumn>
        <S.SmallStartColumn>
          <S.BigText>
            {t('CompanyDetails:contactDetails')}
            <S.EditButton
              appearance="secondary"
              onClick={showContactDetailsModal}
              disabled={!canChangeDetails}
            >
              <Icon as={EditIcon} size="14px" />
            </S.EditButton>
          </S.BigText>
          <Content details={{contactName, email, phone}} />
        </S.SmallStartColumn>
      </Row>
    </>
  );
});

SupportCompanyDetails.displayName = 'SupportCompanyDetails';

export const CompanyDetails = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: companyDetailsData = DEFAULT_COMPANY_DETAILS, isLoading: isCompanyDetailsLoading} =
    useGetCompanyDetailsQuery(supportTenantId);
  const {data: tenantLeadDetails} = useGetTenantLeadDetailsQuery(supportTenantId);

  const hasTenantDetailsReadPermission = usePermission(EPermission.PERMISSION_TENANT_DETAILS_READ);
  const hasTenantDetailsWritePermission = usePermission(
    EPermission.PERMISSION_TENANT_DETAILS_WRITE,
  );
  const canReadDetails = !supportTenantId || hasTenantDetailsReadPermission;
  const canChangeDetails = !supportTenantId || hasTenantDetailsWritePermission;

  const companyDetailsAction = React.useMemo(
    () => getActionName(companyDetailsData),
    [companyDetailsData],
  );

  if (isCompanyDetailsLoading) {
    return <S.LargeSkeleton />;
  }

  if (!canReadDetails) {
    return null;
  }

  return (
    <>
      {companyDetailsAction !== EDetailsAction.UPDATE && (
        <Banner type="ERROR" title={t('CompanyDetails:notification')} />
      )}
      {!!supportTenantId && <SupportCompanyDetails />}
      <S.SmallStartColumn>
        <BigText>{t('CompanyDetails:companyRequisites')}</BigText>
        <Content details={companyDetailsData} countryCode={tenantLeadDetails?.countryCode} />
        <S.Button
          appearance="secondary"
          hasTopIndent
          onClick={() => showCompanyDetailsModal()}
          disabled={!canChangeDetails}
        >
          {t(`CompanyDetails:detailsAction.${companyDetailsAction}`)}
        </S.Button>
      </S.SmallStartColumn>
    </>
  );
});

CompanyDetails.displayName = 'CompanyDetails';
